/**
 * Settings API Client
 *
 * API functions for Epic 9: System Settings & Master Data management.
 */

import { apiRequest, getAccessToken } from './api';
import type { PauzeVenster } from '@functieloonhuis/shared';
import type {
  RoleDTO,
  PermissionDTO,
  CreateRoleRequest,
  UpdateRoleRequest,
  DepartmentDTO,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  FunctionDTO,
  CreateFunctionRequest,
  UpdateFunctionRequest,
  RoleTypeDTO,
  CreateRoleTypeRequest,
  UpdateRoleTypeRequest,
  CompetencyDTO,
  CreateCompetencyRequest,
  UpdateCompetencyRequest,
  ScoreCriteriaDTO,
  UpdateScoreCriteriaRequest,
  ScoreThresholdsDTO,
  AgreementTypeDTO,
  CreateAgreementTypeRequest,
  UpdateAgreementTypeRequest,
  SalaryRangeDTO,
  UpdateSalaryRangeRequest,
  EvaluationCycleDTO,
  CreateEvaluationCycleRequest,
  UpdateEvaluationCycleRequest,
  SystemSettingDTO,
  UpdateSystemSettingsRequest,
  EvaluationSettingsDTO,
  FeatureFlagsDTO,
  HolidayDTO,
  CreateHolidayRequest,
  UpdateHolidayRequest,
  SelfServiceFieldDTO,
  UpdateSelfServiceFieldsRequest,
  EmployeeDropdownDTO,
} from '@functieloonhuis/shared';

// Response types
interface DataResponse<T> {
  data: T;
}

// =============================================================================
// ROLES & PERMISSIONS
// =============================================================================

export const rolesApi = {
  list: async (): Promise<RoleDTO[]> => {
    const res = await apiRequest<DataResponse<RoleDTO[]>>('/settings/roles');
    return res.data;
  },

  get: async (id: number): Promise<RoleDTO> => {
    const res = await apiRequest<DataResponse<RoleDTO>>(`/settings/roles/${id}`);
    return res.data;
  },

  create: async (data: CreateRoleRequest): Promise<RoleDTO> => {
    const res = await apiRequest<DataResponse<RoleDTO>>('/settings/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateRoleRequest): Promise<RoleDTO> => {
    const res = await apiRequest<DataResponse<RoleDTO>>(`/settings/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/roles/${id}`, { method: 'DELETE' });
  },
};

export const permissionsApi = {
  list: async (): Promise<Record<string, PermissionDTO[]>> => {
    const res = await apiRequest<{
      data: PermissionDTO[];
      grouped: Record<string, PermissionDTO[]>;
    }>('/settings/permissions');
    return res.grouped;
  },
};

// =============================================================================
// DEPARTMENTS
// =============================================================================

export const departmentsApi = {
  list: async (): Promise<DepartmentDTO[]> => {
    const res = await apiRequest<DataResponse<DepartmentDTO[]>>('/settings/departments');
    return res.data;
  },

  get: async (id: number): Promise<DepartmentDTO> => {
    const res = await apiRequest<DataResponse<DepartmentDTO>>(`/settings/departments/${id}`);
    return res.data;
  },

  create: async (data: CreateDepartmentRequest): Promise<DepartmentDTO> => {
    const res = await apiRequest<DataResponse<DepartmentDTO>>('/settings/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateDepartmentRequest): Promise<DepartmentDTO> => {
    const res = await apiRequest<DataResponse<DepartmentDTO>>(`/settings/departments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/departments/${id}`, { method: 'DELETE' });
  },
};

// =============================================================================
// FUNCTIONS
// =============================================================================

export const functionsApi = {
  list: async (): Promise<FunctionDTO[]> => {
    const res = await apiRequest<DataResponse<FunctionDTO[]>>('/settings/functions');
    return res.data;
  },

  get: async (id: number): Promise<FunctionDTO> => {
    const res = await apiRequest<DataResponse<FunctionDTO>>(`/settings/functions/${id}`);
    return res.data;
  },

  create: async (data: CreateFunctionRequest): Promise<FunctionDTO> => {
    const res = await apiRequest<DataResponse<FunctionDTO>>('/settings/functions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateFunctionRequest): Promise<FunctionDTO> => {
    const res = await apiRequest<DataResponse<FunctionDTO>>(`/settings/functions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/functions/${id}`, { method: 'DELETE' });
  },
};

// =============================================================================
// ROLE TYPES (Epic 1 — Roltypes & niveau-bepaling v1)
// =============================================================================
// Note: no `delete` — role types can only be deactivated/reactivated, never
// hard-deleted (v1-PRD open vraag 4).

export const roleTypesApi = {
  list: async (includeInactive = false): Promise<RoleTypeDTO[]> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    const res = await apiRequest<DataResponse<RoleTypeDTO[]>>(`/settings/role-types${query}`);
    return res.data;
  },

  get: async (id: number): Promise<RoleTypeDTO> => {
    const res = await apiRequest<DataResponse<RoleTypeDTO>>(`/settings/role-types/${id}`);
    return res.data;
  },

  create: async (data: CreateRoleTypeRequest): Promise<RoleTypeDTO> => {
    const res = await apiRequest<DataResponse<RoleTypeDTO>>('/settings/role-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateRoleTypeRequest): Promise<RoleTypeDTO> => {
    const res = await apiRequest<DataResponse<RoleTypeDTO>>(`/settings/role-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  deactivate: async (id: number): Promise<RoleTypeDTO> => {
    const res = await apiRequest<DataResponse<RoleTypeDTO>>(
      `/settings/role-types/${id}/deactivate`,
      {
        method: 'POST',
      }
    );
    return res.data;
  },

  reactivate: async (id: number): Promise<RoleTypeDTO> => {
    const res = await apiRequest<DataResponse<RoleTypeDTO>>(
      `/settings/role-types/${id}/reactivate`,
      {
        method: 'POST',
      }
    );
    return res.data;
  },
};

// =============================================================================
// COMPETENCIES
// =============================================================================

export const settingsCompetenciesApi = {
  list: async (): Promise<CompetencyDTO[]> => {
    const res = await apiRequest<DataResponse<CompetencyDTO[]>>('/settings/competencies');
    return res.data;
  },

  get: async (id: number): Promise<CompetencyDTO> => {
    const res = await apiRequest<DataResponse<CompetencyDTO>>(`/settings/competencies/${id}`);
    return res.data;
  },

  create: async (data: CreateCompetencyRequest): Promise<CompetencyDTO> => {
    const res = await apiRequest<DataResponse<CompetencyDTO>>('/settings/competencies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateCompetencyRequest): Promise<CompetencyDTO> => {
    const res = await apiRequest<DataResponse<CompetencyDTO>>(`/settings/competencies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/competencies/${id}`, { method: 'DELETE' });
  },
};

// =============================================================================
// SCORE CRITERIA
// =============================================================================

export const scoreCriteriaApi = {
  list: async (contextType?: string, contextId?: number): Promise<ScoreCriteriaDTO[]> => {
    const params = new URLSearchParams();
    if (contextType) params.append('contextType', contextType);
    if (contextId) params.append('contextId', String(contextId));
    const query = params.toString() ? `?${params}` : '';
    const res = await apiRequest<DataResponse<ScoreCriteriaDTO[]>>(
      `/settings/score-criteria${query}`
    );
    return res.data;
  },

  update: async (data: UpdateScoreCriteriaRequest): Promise<ScoreCriteriaDTO[]> => {
    const res = await apiRequest<DataResponse<ScoreCriteriaDTO[]>>('/settings/score-criteria', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  getThresholds: async (): Promise<ScoreThresholdsDTO> => {
    const res = await apiRequest<DataResponse<ScoreThresholdsDTO>>('/settings/score-thresholds');
    return res.data;
  },
};

// =============================================================================
// AGREEMENT TYPES
// =============================================================================

export const agreementTypesApi = {
  list: async (): Promise<AgreementTypeDTO[]> => {
    const res = await apiRequest<DataResponse<AgreementTypeDTO[]>>('/settings/agreement-types');
    return res.data;
  },

  get: async (id: number): Promise<AgreementTypeDTO> => {
    const res = await apiRequest<DataResponse<AgreementTypeDTO>>(`/settings/agreement-types/${id}`);
    return res.data;
  },

  create: async (data: CreateAgreementTypeRequest): Promise<AgreementTypeDTO> => {
    const res = await apiRequest<DataResponse<AgreementTypeDTO>>('/settings/agreement-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateAgreementTypeRequest): Promise<AgreementTypeDTO> => {
    const res = await apiRequest<DataResponse<AgreementTypeDTO>>(
      `/settings/agreement-types/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/agreement-types/${id}`, { method: 'DELETE' });
  },
};

// =============================================================================
// SALARY RANGES
// =============================================================================

export const salaryRangesApi = {
  list: async (): Promise<SalaryRangeDTO[]> => {
    const res = await apiRequest<DataResponse<SalaryRangeDTO[]>>('/settings/salary-ranges');
    return res.data;
  },

  update: async (niveauId: number, data: UpdateSalaryRangeRequest): Promise<SalaryRangeDTO> => {
    const res = await apiRequest<DataResponse<SalaryRangeDTO>>(
      `/settings/salary-ranges/${niveauId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return res.data;
  },
};

// =============================================================================
// EVALUATION CYCLES
// =============================================================================

export const evaluationCyclesApi = {
  list: async (): Promise<EvaluationCycleDTO[]> => {
    const res = await apiRequest<DataResponse<EvaluationCycleDTO[]>>('/settings/cycles');
    return res.data;
  },

  get: async (id: number): Promise<EvaluationCycleDTO> => {
    const res = await apiRequest<DataResponse<EvaluationCycleDTO>>(`/settings/cycles/${id}`);
    return res.data;
  },

  create: async (data: CreateEvaluationCycleRequest): Promise<EvaluationCycleDTO> => {
    const res = await apiRequest<DataResponse<EvaluationCycleDTO>>('/settings/cycles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateEvaluationCycleRequest): Promise<EvaluationCycleDTO> => {
    const res = await apiRequest<DataResponse<EvaluationCycleDTO>>(`/settings/cycles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/cycles/${id}`, { method: 'DELETE' });
  },

  activate: async (id: number): Promise<EvaluationCycleDTO> => {
    const res = await apiRequest<DataResponse<EvaluationCycleDTO>>(
      `/settings/cycles/${id}/activate`,
      {
        method: 'POST',
      }
    );
    return res.data;
  },
};

// =============================================================================
// SYSTEM SETTINGS
// =============================================================================

export const systemSettingsApi = {
  list: async (category?: string): Promise<SystemSettingDTO[]> => {
    const query = category ? `?category=${category}` : '';
    const res = await apiRequest<DataResponse<SystemSettingDTO[]>>(`/settings/system${query}`);
    return res.data;
  },

  update: async (data: UpdateSystemSettingsRequest): Promise<SystemSettingDTO[]> => {
    const res = await apiRequest<DataResponse<SystemSettingDTO[]>>('/settings/system', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  getEvaluationSettings: async (): Promise<EvaluationSettingsDTO> => {
    const res = await apiRequest<DataResponse<EvaluationSettingsDTO>>(
      '/settings/system/evaluation'
    );
    return res.data;
  },

  getFeatureFlags: async (): Promise<FeatureFlagsDTO> => {
    const res = await apiRequest<DataResponse<FeatureFlagsDTO>>('/settings/system/features');
    return res.data;
  },

  // Epic 1 — Verlof correctie/kort verlof, Story 1.1, AC-5. Voor elke
  // ingelogde gebruiker leesbaar (geen categorie-dump zoals `/settings/system`,
  // dat admin-only is) — het aanvraagformulier (story 1.3) heeft dit venster
  // nodig om uren te berekenen.
  getPauzeVenster: async (): Promise<PauzeVenster> => {
    const res = await apiRequest<DataResponse<PauzeVenster>>('/settings/verlof/pauze-venster');
    return res.data;
  },

  // Story 5.4 (Verlofsoorten, AC-10): de dag vanaf wanneer de nieuwe
  // verlofsoorten gelden. Net als het pauzevenster hierboven leesbaar voor
  // elke ingelogde gebruiker — het aanvraagformulier moet weten of de
  // soortkeuze verschijnt.
  //
  // `null` betekent: niet gezet, en dus gelden de nieuwe regels niet. Er komt
  // hier geen standaarddatum voor in de plaats (KE-8).
  getVerlofsoortIngangsdatum: async (): Promise<string | null> => {
    const res = await apiRequest<DataResponse<string | null>>(
      '/settings/verlof/verlofsoort-ingangsdatum'
    );
    return res.data;
  },
};

// =============================================================================
// HOLIDAYS
// =============================================================================

export const holidaysApi = {
  list: async (year?: number): Promise<HolidayDTO[]> => {
    const query = year ? `?year=${year}` : '';
    const res = await apiRequest<DataResponse<HolidayDTO[]>>(`/settings/holidays${query}`);
    return res.data;
  },

  create: async (data: CreateHolidayRequest): Promise<HolidayDTO> => {
    const res = await apiRequest<DataResponse<HolidayDTO>>('/settings/holidays', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  update: async (id: number, data: UpdateHolidayRequest): Promise<HolidayDTO> => {
    const res = await apiRequest<DataResponse<HolidayDTO>>(`/settings/holidays/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiRequest(`/settings/holidays/${id}`, { method: 'DELETE' });
  },
};

// =============================================================================
// SELF-SERVICE FIELDS
// =============================================================================

export const selfServiceFieldsApi = {
  list: async (): Promise<SelfServiceFieldDTO[]> => {
    const res = await apiRequest<DataResponse<SelfServiceFieldDTO[]>>('/settings/self-service');
    return res.data;
  },

  update: async (data: UpdateSelfServiceFieldsRequest): Promise<SelfServiceFieldDTO[]> => {
    const res = await apiRequest<DataResponse<SelfServiceFieldDTO[]>>('/settings/self-service', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  },
};

// =============================================================================
// AUDIT LOG
// =============================================================================

export interface AuditLogDTO {
  id: number;
  action: string;
  entity: string;
  entityId: number | null;
  userId: number;
  userName: string | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AuditLogQueryParams {
  action?: string;
  userId?: number;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogResponse {
  data: AuditLogDTO[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export const auditLogApi = {
  list: async (params?: AuditLogQueryParams): Promise<AuditLogResponse> => {
    const query = new URLSearchParams();
    if (params?.action) query.append('action', params.action);
    if (params?.userId) query.append('userId', String(params.userId));
    if (params?.fromDate) query.append('fromDate', params.fromDate);
    if (params?.toDate) query.append('toDate', params.toDate);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    const queryStr = query.toString() ? `?${query}` : '';
    const res = await apiRequest<AuditLogResponse>(`/settings/audit-log${queryStr}`);
    return res;
  },
};

// =============================================================================
// EMPLOYEES (for dropdowns)
// =============================================================================

export const employeesDropdownApi = {
  list: async (): Promise<EmployeeDropdownDTO[]> => {
    const res = await apiRequest<DataResponse<EmployeeDropdownDTO[]>>('/settings/employees');
    return res.data;
  },
};

// =============================================================================
// IMPORT/EXPORT
// =============================================================================

export type ImportExportType = 'employees' | 'tasks' | 'competencies' | 'departments' | 'functions';

export interface ImportPreviewResult {
  valid: boolean;
  totalRows: number;
  validRows: number;
  errorRows: { row: number; errors: string[] }[];
  preview: Record<string, unknown>[];
}

export interface ImportExecuteResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
}

export const importExportApi = {
  exportCsv: async (type: ImportExportType): Promise<Blob> => {
    const response = await fetch(`/api/v1/settings/export/${type}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    return response.blob();
  },

  downloadTemplate: async (type: Exclude<ImportExportType, 'employees'>): Promise<Blob> => {
    const response = await fetch(`/api/v1/settings/export/${type}/template`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Template download failed');
    }
    return response.blob();
  },

  previewImport: async (
    type: Exclude<ImportExportType, 'employees'>,
    csvData: string
  ): Promise<ImportPreviewResult> => {
    const res = await apiRequest<DataResponse<ImportPreviewResult>>('/settings/import/preview', {
      method: 'POST',
      body: JSON.stringify({ type, data: csvData }),
    });
    return res.data;
  },

  executeImport: async (
    type: Exclude<ImportExportType, 'employees'>,
    csvData: string
  ): Promise<ImportExecuteResult> => {
    const res = await apiRequest<DataResponse<ImportExecuteResult>>('/settings/import/execute', {
      method: 'POST',
      body: JSON.stringify({ type, data: csvData }),
    });
    return res.data;
  },
};

// =============================================================================
// DATABASE BACKUPS
// =============================================================================

export interface BackupDTO {
  filename: string;
  createdAt: string;
  size: number;
  sizeFormatted: string;
  comment: string | null;
  createdBy: string | null;
  createdById: number | null;
}

export interface CreateBackupRequest {
  comment?: string;
}

export interface RestoreBackupRequest {
  password: string;
}

export interface RestoreBackupResponse {
  success: boolean;
  message: string;
  preRestoreBackup: string;
}

export interface UploadRestoreResponse {
  success: boolean;
  message: string;
  preRestoreBackup: string;
  uploadedBackup: string;
}

export const backupsApi = {
  list: async (): Promise<BackupDTO[]> => {
    const res = await apiRequest<DataResponse<BackupDTO[]>>('/backups');
    return res.data;
  },

  create: async (data?: CreateBackupRequest): Promise<BackupDTO> => {
    const res = await apiRequest<DataResponse<BackupDTO>>('/backups', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    return res.data;
  },

  download: async (filename: string): Promise<void> => {
    const token = getAccessToken();
    const response = await fetch(`/api/v1/backups/${filename}/download`, {
      method: 'GET',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error('Download failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  delete: async (filename: string): Promise<void> => {
    await apiRequest(`/backups/${filename}`, { method: 'DELETE' });
  },

  restore: async (filename: string, data: RestoreBackupRequest): Promise<RestoreBackupResponse> => {
    const res = await apiRequest<RestoreBackupResponse>(`/backups/${filename}/restore`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res;
  },

  upload: async (file: File, comment?: string): Promise<BackupDTO> => {
    const token = getAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    if (comment) {
      formData.append('comment', comment);
    }

    const response = await fetch('/api/v1/backups/upload', {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload mislukt' }));
      throw new Error(error.error || 'Upload mislukt');
    }

    const res = await response.json();
    return res.data;
  },

  uploadAndRestore: async (
    file: File,
    password: string,
    comment?: string
  ): Promise<UploadRestoreResponse> => {
    const token = getAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('password', password);
    if (comment) {
      formData.append('comment', comment);
    }

    const response = await fetch('/api/v1/backups/upload-restore', {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload en herstel mislukt' }));
      throw new Error(error.error || 'Upload en herstel mislukt');
    }

    return response.json();
  },
};
